import { useCallback, useMemo, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiButton,
  MiIcon,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  CONTACT_CARD_EDIT_MODES,
  CONTACT_DISPLAY_TYPE,
  MODAL_TITLES,
  PERMISSIONS,
  PREFERRED_CONTACT_METHOD,
} from "@miplatform/constants";
import { getFullName, getInitials, getPhoneNumber } from "@miplatform/utils";
import {
  mdiAccount,
  mdiAccountGroupOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiEmail,
  mdiPencilOutline,
  mdiPhone,
  mdiStar,
  mdiTrashCanOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Collapse, Tooltip, Typography } from "@mui/material";

/**
 * MiContactInfoCard component for displaying and managing contact information.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.isLoading - Indicates if the component is in a loading state.
 * @param {boolean} props.enableDeleteRelationship - Enables the delete relationship action.
 * @param {Object} [props.contact={}] - The contact object containing contact information.
 * @param {Function} [props.onEdit=() => null] - Function to handle edit actions.
 * @param {boolean} props.showMakePrimary - Shows the make primary action.
 * @param {boolean} [props.abbreviated=false] - Displays an abbreviated version of the component if true.
 * @param {string|Array} props.editMode - Determines the edit mode(s) available for the contact.
 * @param {Array} [props.relationships=contact.relationships] - List of relationships associated with the contact.
 * @param {Function} [props.onManageRelationships] - Function to handle managing relationships.
 * @param {Function} [props.onMakePrimary] - Function to handle making the contact primary.
 * @param {Function} [props.onDeleteRelationship] - Function to handle deleting a relationship.
 *
 * @returns {JSX.Element} The rendered contact info card component.
 *
 * @example
 * <MiContactInfoCard
 *   isLoading={isLoading}
 *   enableDeleteRelationship={true}
 *   contact={contactData}
 *   onEdit={handleEdit}
 *   showMakePrimary={true}
 *   abbreviated={false}
 *   editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
 *   relationships={relationshipsData}
 *   onManageRelationships={handleManageRelationships}
 *   onMakePrimary={handleMakePrimary}
 *   onDeleteRelationship={handleDeleteRelationship}
 * />
 */
export const MiContactInfoCard = ({
  isLoading,
  enableDeleteRelationship,
  contact = {},
  onEdit = () => null,
  showMakePrimary,
  abbreviated = false,
  editMode,
  relationships = contact.relationships,
  onManageRelationships,
  onMakePrimary,
  onDeleteRelationship,
  onDeleteRelationshipOverwrite,
}) => {
  const containerRef = useRef(null);

  const { hasPermission } = usePermissions();
  const { theme, classes } = useStyles();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const fullName =
    contact.resourceName ||
    contact.name ||
    getFullName(contact.firstName, contact.lastName);

  const isPhonePreferred =
    contact.preferredContactMethod === PREFERRED_CONTACT_METHOD.Phone;

  const relationship =
    contact.label ||
    contact.title ||
    contact.contactTypeName ||
    contact.resourceTitle;

  const actions = useMemo(() => {
    const currentActions = [];

    if (showMakePrimary && !contact.isPrimary) {
      currentActions.push({
        label: "Make Primary",
        icon: mdiStar,
        disabled: isLoading,
        "data-testid": "make-primary-button",
        action: onMakePrimary,
      });
    }

    if (relationships?.length > 0) {
      currentActions.push({
        label: "Manage Relationships",
        icon: mdiAccountGroupOutline,
        "data-testid": "open-manage-relationships-button",
        action: onManageRelationships,
      });
    }

    if (
      editMode === CONTACT_CARD_EDIT_MODES.RELATIONSHIP ||
      (Array.isArray(editMode) &&
        editMode.includes(CONTACT_CARD_EDIT_MODES.RELATIONSHIP))
    ) {
      currentActions.push({
        label: "Edit Relationship",
        icon: mdiAccount,
        "data-testid": "open-edit-relationship-button",
        action: () => onEdit(CONTACT_CARD_EDIT_MODES.RELATIONSHIP),
      });
    }

    if (
      editMode === CONTACT_CARD_EDIT_MODES.CONTACT ||
      (Array.isArray(editMode) &&
        editMode.includes(CONTACT_CARD_EDIT_MODES.CONTACT))
    ) {
      currentActions.push({
        label: "Edit Contact",
        icon: mdiPencilOutline,
        "data-testid": "open-edit-button",
        action: () => onEdit(CONTACT_CARD_EDIT_MODES.CONTACT),
      });
    }

    if (enableDeleteRelationship) {
      currentActions.push({
        label: "Delete Relationship",
        icon: mdiTrashCanOutline,
        color: theme.palette.error.main,
        disabled: isLoading,
        "data-testid": "open-edit-button",
        action: () => setIsDeleteModalOpen(true),
      });
    }

    return currentActions;
  }, [showMakePrimary, isLoading, relationships]);

  const renderEmail = useCallback(
    (showPreferred) =>
      contact.email && (
        <Typography
          className={classes.secondaryContactInfo}
          sx={{ textDecoration: "none" }}
          component="a"
          href={`mailto: ${contact.email}`}
        >
          <Icon
            path={mdiEmail}
            size={0.7}
            color={theme.palette.primary.main}
            data-testid="email-icon"
          />
          {contact.email}
          {showPreferred && (
            <Typography className={classes.preferredBadge}>
              Preferred
            </Typography>
          )}
        </Typography>
      ),
    [contact.email]
  );

  const renderPhone = useCallback(
    (showPreferred, isSecondary = false) => {
      const phoneNumber = isSecondary ? contact.secondaryPhone : contact.phone;

      return (
        phoneNumber && (
          <Typography
            className={classes.secondaryContactInfo}
            sx={{ textDecoration: "none" }}
            component="a"
            href={`tel: ${phoneNumber}`}
          >
            <Icon
              path={mdiPhone}
              size={0.7}
              color={
                isSecondary
                  ? theme.palette.grey.main
                  : theme.palette.primary.main
              }
              data-testid="phone-icon"
            />
            {getPhoneNumber(phoneNumber)}
            {showPreferred && (
              <Box className={classes.preferredBadge}>Preferred</Box>
            )}
          </Typography>
        )
      );
    },

    [contact.phone, contact.secondaryPhone]
  );

  return (
    <Box
      data-testid="contact-info-card"
      ref={containerRef}
      className={classes.infoContainer}
    >
      <Box className={classes.leftInfo}>
        <Box data-testid="avatar-box" position="relative">
          <MiAvatar data-testid="avatar-icon" className={classes.avatar}>
            {getInitials(fullName)}
          </MiAvatar>
        </Box>
        <Box className={classes.info}>
          <Typography className={classes.secondaryContactInfo}>
            {relationship}
          </Typography>
          <Box className={classes.nameContainer}>
            <Tooltip title={fullName}>
              <Typography
                fontWeight={theme.fontWeight.semiBold}
                fontSize={theme.fontSize.medium}
                width="fit-content"
                {...(containerRef.current && {
                  maxWidth: containerRef.current.offsetWidth * 0.7,
                  noWrap: true,
                })}
              >
                {fullName}
              </Typography>
            </Tooltip>
            {contact?.level && (
              <Typography className={classes.contactLevelBadge}>
                {CONTACT_DISPLAY_TYPE[contact?.level]}
              </Typography>
            )}
            {contact.isVip && (
              <Typography className={classes.badge}>VIP</Typography>
            )}
            {contact?.isPrimary && (
              <MiIcon
                path={mdiStar}
                size={0.7}
                color={theme.palette.primary.main}
                data-testid="contact-is-primary-badge"
              />
            )}
          </Box>
          {!abbreviated && (
            <>
              {isPhonePreferred
                ? renderPhone(isExtended)
                : renderEmail(isExtended)}
              <Collapse in={isExtended} unmountOnExit>
                {isPhonePreferred ? renderEmail(false) : renderPhone(false)}
                {renderPhone(false, true)}
              </Collapse>
            </>
          )}
        </Box>
      </Box>
      <Box className={classes.rightActionsContainer}>
        {actions.length && hasPermission(PERMISSIONS.CONTACT.UPDATE) ? (
          <MiMenu data={actions} />
        ) : null}
        {(contact.email || contact.secondaryPhone) && (
          <MiButton
            variant="text"
            color={BUTTON_COLORS.GREY}
            startIcon={{
              path: isExtended ? mdiChevronUp : mdiChevronDown,
              iconSize: "16px",
            }}
            onClick={() => setIsExtended(!isExtended)}
            data-testid="extend-card-button"
          />
        )}
      </Box>
      <MiModalConfirm
        open={isDeleteModalOpen}
        textPositive="Delete"
        title={MODAL_TITLES.DELETE}
        handleNegative={() => setIsDeleteModalOpen(false)}
        handlePositive={() => {
          (onDeleteRelationshipOverwrite || onDeleteRelationship)(contact);
          setIsDeleteModalOpen(false);
        }}
        description={`Are you sure you want to delete relationship ${relationship} associated with ${contact.firstName} ${contact.lastName}?`}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  info: {
    display: "flex",
    flexDirection: "column",
  },
  infoContainer: {
    position: "relative",
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "start",
    justifyContent: "space-between",
  },
  rightActionsContainer: {
    display: "flex",
    alignItems: "start",
  },
  loader: {
    position: "absolute",
    top: 0,
  },
  avatar: {
    ...theme.typography.body1,
    width: "34px",
    height: "34px",
    color: theme.palette.primary.white,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0, 1),
    gap: theme.spacing(2),
    marginTop: 5,
  },
  leftInfo: {
    gap: theme.spacing(2),
    display: "flex",
    alignItems: "start",
    width: "75%",
  },
  preferredBadge: {
    fontSize: theme.fontSize.small,
    textDecoration: "none",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
    borderRadius: 4,
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1),
  },
  contactLevelBadge: {
    fontSize: theme.fontSize.small,
    backgroundColor: theme.palette.grey.lightAccent,
    borderRadius: 4,
    padding: theme.spacing(0, 1),
  },
  nameContainer: {
    display: "flex",
    flexDirections: "row",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    alignItems: "center",
    gap: theme.spacing(1),
  },
  badge: {
    fontSize: theme.fontSize.small,
    borderRadius: "4px",
    padding: theme.spacing(0, 1),
    backgroundColor: theme.palette.yellow.medium,
    color: theme.palette.white.main,
  },
  secondaryContactInfo: {
    color: theme.palette.grey.main,
    fontSize: theme.fontSize.small,
    display: "flex",
    gap: theme.spacing(1),
  },
}));
